import React, { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  Upload
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  FilePdfOutlined,
  PaperClipOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import SignatureField from "./SignatureField";
import {
  completeSession,
  deleteSession,
  deleteSessionAttachment,
  getMeta,
  getSession,
  getTopics,
  sessionFileUrl,
  sessionPdfUrl,
  signParticipant,
  updateSession,
  uploadSessionAttachment
} from "./api";
import { formatDateTime } from "./format";
import "./safety.css";

/** The participant reads the topics and signs on the device that is passed round. */
const SignDialog = ({ session, participant, onClose, onSigned }) => {
  const { t } = useTranslation("dashboard");
  const pad = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const sign = async () => {
    setError("");
    if (pad.current.isEmpty()) return setError(t("Please sign in the box."));
    setBusy(true);
    try {
      const res = await signParticipant(session._id, {
        employee: participant.employee,
        image: pad.current.toDataURL()
      });
      onSigned(res.data.data);
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="safety-page"
      open={Boolean(participant)}
      width={680}
      title={participant ? participant.name : ""}
      onCancel={onClose}
      destroyOnClose
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("Cancel")}
        </Button>,
        <Button key="sign" type="primary" icon={<EditOutlined />} loading={busy} onClick={sign}>
          {t("Sign")}
        </Button>
      ]}
    >
      <Typography.Paragraph strong>
        {session.topics.map(topic => topic.title).join(", ")}
      </Typography.Paragraph>
      <Typography.Paragraph
        type="secondary"
        className="sf-content-preview"
        ellipsis={{ rows: 6, expandable: true, symbol: t("more") }}
      >
        {session.content}
      </Typography.Paragraph>
      <SignatureField
        ref={pad}
        label={t("I took part in this instruction and understood its content.")}
      />
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
};

const CompleteDialog = ({ open, session, onClose, onDone }) => {
  const { t } = useTranslation("dashboard");
  const pad = useRef(null);
  const [busy, setBusy] = useState(false);
  const unsigned = session.participants.filter(p => !p.signature?.signedAt);

  const complete = async () => {
    setBusy(true);
    try {
      const image = pad.current && !pad.current.isEmpty() ? pad.current.toDataURL() : undefined;
      const res = await completeSession(session._id, { image });
      if (res.data.pdfArchived === false) {
        Modal.warning({ title: t("Completed, but the PDF could not be archived.") });
      }
      onDone(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="safety-page"
      open={open}
      width={640}
      title={t("Complete safety instruction")}
      onCancel={onClose}
      destroyOnClose
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("Cancel")}
        </Button>,
        <Button key="ok" type="primary" loading={busy} onClick={complete}>
          {t("Complete and archive")}
        </Button>
      ]}
    >
      {unsigned.length > 0 && (
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message={t("Not signed, recorded as absent: {{names}}", {
            names: unsigned.map(p => p.name).join(", ")
          })}
        />
      )}
      <Typography.Paragraph type="secondary">
        {t(
          "After completing, the record can no longer be changed. The PDF is archived in the documents."
        )}
      </Typography.Paragraph>
      <SignatureField ref={pad} label={t("Signature of the instructor (optional)")} />
    </Modal>
  );
};

/** One Unterweisung: details, material, participants signing one after another, completion. */
const SessionEditor = () => {
  const { t, i18n } = useTranslation("dashboard");
  const { id } = useParams();
  const history = useHistory();
  const [form] = Form.useForm();
  const [meta, setMeta] = useState(null);
  const [topics, setTopics] = useState([]);
  const [session, setSession] = useState(null);
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(null);
  const [completeOpen, setCompleteOpen] = useState(false);

  const show = data => {
    setSession(data);
    form.setFieldsValue({
      date: data.date ? dayjs(data.date) : null,
      startTime: data.startTime,
      durationMinutes: data.durationMinutes,
      location: data.location,
      instructor: data.instructor || "",
      topics: data.topics.map(topic => topic.topic),
      content: data.content,
      participants: data.participants.map(p => p.employee)
    });
  };

  useEffect(() => {
    Promise.all([getMeta(), getTopics(), getSession(id)])
      .then(([metaRes, topicsRes, sessionRes]) => {
        setMeta(metaRes.data);
        setTopics(topicsRes.data.data);
        show(sessionRes.data.data);
      })
      .catch(generalErrorAlert);
  }, [id]);

  if (!session || !meta) {
    return (
      <div className="safety-page sf-center">
        <Spin />
      </div>
    );
  }

  const completed = session.status === "completed";
  const canEdit = meta.can.update && !completed;
  const contentLocked = session.contentLocked;

  const save = async values => {
    setSaving(true);
    try {
      const body = { participants: values.participants };
      if (!contentLocked) {
        Object.assign(body, {
          date: values.date.format("YYYY-MM-DD"),
          startTime: values.startTime || "",
          durationMinutes: values.durationMinutes || 0,
          location: values.location || "",
          instructor: values.instructor || null,
          topics: values.topics,
          content: values.content || ""
        });
      }
      const res = await updateSession(session._id, body);
      show(res.data.data);
      Success(t("Saved"));
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setSaving(false);
    }
  };

  const upload = async ({ file, onSuccess, onError }) => {
    try {
      const res = await uploadSessionAttachment(session._id, file);
      show(res.data.data);
      onSuccess();
    } catch (error) {
      generalErrorAlert(error);
      onError(error);
    }
  };

  const removeAttachment = async attachmentId => {
    try {
      const res = await deleteSessionAttachment(session._id, attachmentId);
      show(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const remove = async () => {
    try {
      await deleteSession(session._id);
      history.push("/user/safety?tab=sessions");
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const employees = meta.employees || [];

  return (
    <div className="safety-page">
      <div className="sf-header mb-3">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => history.push("/user/safety?tab=sessions")}
        />
        <div className="sf-header-title">
          <Typography.Title level={4}>{session.number}</Typography.Title>
          {completed ? (
            <Tag color="success">{t("Completed")}</Tag>
          ) : (
            <Tag color="processing">
              {t("{{signed}} of {{total}} signed", {
                signed: session.signedCount,
                total: session.participants.length
              })}
            </Tag>
          )}
        </div>
        <Space wrap>
          <Button icon={<FilePdfOutlined />} href={sessionPdfUrl(session._id)} target="_blank">
            PDF
          </Button>
          {canEdit && session.signedCount > 0 && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => setCompleteOpen(true)}
            >
              {t("Complete")}
            </Button>
          )}
          {meta.can.delete && !completed && session.signedCount === 0 && (
            <Popconfirm title={t("Delete this safety instruction?")} onConfirm={remove}>
              <Button danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      </div>

      {contentLocked && !completed && (
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message={t(
            "Participants have signed: the content is locked. You can still add participants."
          )}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title={t("Participants")} size="small" className="mb-3">
            <List
              dataSource={session.participants}
              locale={{ emptyText: t("No participants yet") }}
              renderItem={p => (
                <List.Item
                  actions={[
                    p.signature?.signedAt ? (
                      <Tag key="s" color="success">
                        {formatDateTime(p.signature.signedAt, i18n.language)}
                      </Tag>
                    ) : p.absent ? (
                      <Tag key="a">{t("Absent")}</Tag>
                    ) : canEdit ? (
                      <Button
                        key="b"
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => setSigning(p)}
                      >
                        {t("Sign")}
                      </Button>
                    ) : null
                  ]}
                >
                  <span className="sf-wrap">{p.name}</span>
                </List.Item>
              )}
            />
          </Card>

          <Card title={t("Material")} size="small">
            <List
              dataSource={session.attachments}
              locale={{ emptyText: t("No files") }}
              renderItem={a => (
                <List.Item
                  actions={
                    canEdit && !contentLocked
                      ? [
                          <Button
                            key="d"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            aria-label={t("Delete")}
                            onClick={() => removeAttachment(a._id)}
                          />
                        ]
                      : []
                  }
                >
                  <a href={sessionFileUrl(session._id, a._id)} target="_blank" rel="noreferrer">
                    <PaperClipOutlined /> {a.name}
                  </a>
                </List.Item>
              )}
            />
            {canEdit && !contentLocked && (
              <Upload
                customRequest={upload}
                showUploadList={false}
                accept=".pdf,.jpg,.jpeg,.png,.webp,.docx,.pptx"
              >
                <Button icon={<PaperClipOutlined />}>{t("Add file")}</Button>
              </Upload>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title={t("Details")} size="small">
            <Form form={form} layout="vertical" onFinish={save} disabled={!canEdit}>
              <Form.Item name="topics" label={t("Topics")} rules={[{ required: true }]}>
                <Select
                  mode="multiple"
                  disabled={contentLocked}
                  optionFilterProp="label"
                  options={topics
                    .filter(
                      topic => topic.active || session.topics.some(s => s.topic === topic._id)
                    )
                    .map(topic => ({ value: topic._id, label: topic.title }))}
                />
              </Form.Item>
              <Row gutter={[12, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item name="date" label={t("Date")} rules={[{ required: true }]}>
                    <DatePicker
                      className="sf-full"
                      format="DD.MM.YYYY"
                      allowClear={false}
                      disabled={contentLocked}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Item name="startTime" label={t("Time")}>
                    <Input placeholder="07:00" disabled={contentLocked} />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Item name="durationMinutes" label={t("Minutes")}>
                    <InputNumber className="sf-full" min={0} max={1440} disabled={contentLocked} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="instructor" label={t("Instructed by")}>
                <Select
                  disabled={contentLocked}
                  options={[
                    { value: "", label: t("Company owner") },
                    ...employees.map(e => ({ value: e._id, label: e.name }))
                  ]}
                />
              </Form.Item>
              <Form.Item name="location" label={t("Location")}>
                <Input maxLength={300} disabled={contentLocked} />
              </Form.Item>
              <Form.Item name="content" label={t("Content of the instruction")}>
                <Input.TextArea autoSize={{ minRows: 6, maxRows: 18 }} disabled={contentLocked} />
              </Form.Item>
              <Form.Item name="participants" label={t("Participants")}>
                <Select
                  mode="multiple"
                  optionFilterProp="label"
                  options={employees.map(e => ({
                    value: e._id,
                    label: e.name,
                    disabled: session.participants.some(
                      p => p.employee === e._id && p.signature?.signedAt
                    )
                  }))}
                />
              </Form.Item>
              {canEdit && (
                <Button type="primary" htmlType="submit" loading={saving} block>
                  {t("Save")}
                </Button>
              )}
            </Form>
            {completed && (
              <Typography.Paragraph type="secondary" className="sf-small mt-3">
                {t("Completed on {{date}}", {
                  date: formatDateTime(session.completedAt, i18n.language)
                })}
              </Typography.Paragraph>
            )}
          </Card>
        </Col>
      </Row>

      <SignDialog
        session={session}
        participant={signing}
        onClose={() => setSigning(null)}
        onSigned={data => {
          setSigning(null);
          show(data);
        }}
      />
      <CompleteDialog
        open={completeOpen}
        session={session}
        onClose={() => setCompleteOpen(false)}
        onDone={data => {
          setCompleteOpen(false);
          show(data);
          Success(t("Safety instruction completed"));
        }}
      />
    </div>
  );
};

export default SessionEditor;
